//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// autopilot-suggestion-enable-meeting imports
import { IState, IProps, IContainer } from "./AutopilotSuggestionEnableMeeting.d";
import * as s from "./AutopilotSuggestionEnableMeeting.styles";
import { getGoalStringsFromGoals, getIconTypeForGoal } from "../AutopilotSuggestionsCard.util";
import { fragments } from "./AutopilotSuggestionEnableMeeting.gql";
// other components
import { H2 } from "@ui";
// util
import { createEventParentRelayIdFromOrgRelayId, eventHasRoom } from "@util/event.util";
import { generateEventFormSaveTags, EFlex } from "@util/event-tag.util";
import { ZonedMoment } from "@util/time-zone.util";
import {
  getAbsoluteDateTime,
  getAbsoluteDate,
} from "@clockwise/client-commons/src/util/format-time";
import { track, TrackingEvents } from "@util/analytics.util";
import { logger } from "@util/logger.util";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
// state
import { IReduxState } from "@state/reducers/root.reducer";
import { snackBarOpen } from "@actions/snack-bar.actions";
import {
  addAndRemoveOrgTags,
  markDefragValueKnobsAsPresented,
  markDefragValueKnobsAsAccepted,
} from "@mutations";
// material-ui
import { Button } from "@material-ui/core";
import { Error, NotInterested, Assistant } from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
class AutopilotSuggestionEnableMeetingComponent extends React.PureComponent<IProps, IState> {
  static contextType = TranslationContext;
  declare context: React.ContextType<typeof TranslationContext>;

  constructor(props: IProps) {
    super(props);
    this.state = {
      enabling: false,
      enabled: false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  public componentDidMount() {
    if (this.props.active) {
      this.markAsPresented();
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (nextProps.active && !this.props.active) {
      this.markAsPresented();
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private onSuccess = () => {
    this.setState({ enabling: false, enabled: true });
    this.props.snackBarOpen("Saved event preferences");
    this.markAsAccepted();
  };

  private onFailure = () => {
    this.setState({ enabling: false });
    logger.error("Failed to save event preferences in Autopilot suggestions");
    this.props.snackBarOpen("Failed to save event preferences.", "error");
  };

  private enableAutopilot = (event: ISchema.IEvent) => {
    this.setState({ enabling: true });
    const { relay, org, autopilotMeetingKnob, calendarId } = this.props;
    autopilotMeetingKnob.knobUrn.value;

    // actually enable autopilot
    const eventParentRelayId = createEventParentRelayIdFromOrgRelayId(
      org.id,
      autopilotMeetingKnob.externalEventId,
    );
    if (!eventParentRelayId) {
      return;
    }

    // create tags for saving
    const hasRoom = eventHasRoom(event);

    const flex =
      (event.suggestedAutopilotSettingsResponseV3.__typename !== "GraphEntityError" &&
        ((event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings
          .movementType as unknown) as EFlex)) ||
      EFlex.Day;

    const allowSimilarRooms =
      (event.suggestedAutopilotSettingsResponseV3.__typename !== "GraphEntityError" &&
        event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings.isRoomFlexible) ||
      hasRoom;

    const allowedStart =
      (event.suggestedAutopilotSettingsResponseV3.__typename !== "GraphEntityError" &&
        event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings.allowedStartTime) ||
      null;

    const allowedEnd =
      (event.suggestedAutopilotSettingsResponseV3.__typename !== "GraphEntityError" &&
        event.suggestedAutopilotSettingsResponseV3.suggestedAutopilotSettings.allowedEndTime) ||
      null;

    const tags = generateEventFormSaveTags(flex, undefined, undefined, allowSimilarRooms);

    // add the org id and eventparent id to the input
    const input: ISchema.IAddAndRemoveOrgTagsMutationInput = {
      calendarId,
      eventParentRelayId,
      ...tags,
      moveRangeV3: {
        allowedStartTimeInclusive: allowedStart!,
        allowedEndTimeInclusive: allowedEnd!,
      },
      orgRelayId: org.id,
    };

    // finally, mutate
    addAndRemoveOrgTags(relay.environment, input, this.onSuccess, this.onFailure);

    // callback
    this.props.onAction();
    track(TrackingEvents.AUTOPILOT_SUGGESTIONS.AUTOPILOT_SUGGESTION_ENABLE_MEETING_CLICKED);
  };

  private onMarkAsPresentedSuccess = () => {
    this.props.onMarkAsPresentedCallback(this.props.autopilotMeetingKnob.knobUrn);
  };

  private onMarkAsPresentedFailure = () => {
    logger.error("Failed to mark autopilot suggestion enable meeting as presented");
  };

  private markAsPresented = () => {
    markDefragValueKnobsAsPresented(
      this.props.relay.environment,
      {
        knobUrns: [this.props.autopilotMeetingKnob.knobUrn],
        orgRelayId: this.props.org.id,
      },
      this.onMarkAsPresentedSuccess,
      this.onMarkAsPresentedFailure,
    );
  };

  private onMarkAsAcceptedFailure = () => {
    logger.error("Failed to mark autopilot invite suggestion as accepted");
  };

  private markAsAccepted = () => {
    markDefragValueKnobsAsAccepted(
      this.props.relay.environment,
      {
        knobUrns: [this.props.autopilotMeetingKnob.knobUrn],
        orgRelayId: this.props.org.id,
      },
      () => null,
      this.onMarkAsAcceptedFailure,
    );
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderAction = (event: ISchema.IEvent) => {
    const t = this.context;
    const { classes } = this.props;

    return (
      <Button
        fullWidth
        variant="contained"
        color="primary"
        onClick={() => this.enableAutopilot(event)}
        classes={{ root: classes.button }}
        disabled={this.state.enabling || this.state.enabled}
      >
        <Assistant {...s.buttonIcon} />
        <div>{t("autopilot-suggestions:toggle-button", { on: this.state.enabled })}</div>
      </Button>
    );
  };

  private renderPain = () => {
    const { classes, autopilotMeetingKnob } = this.props;
    const goalStrings = [...new Set(getGoalStringsFromGoals(autopilotMeetingKnob.affectedGoals))];

    return (
      <div className={classes.painContainer}>
        {goalStrings.map((goal, i) => {
          return (
            <div key={`${goal}-${i}`} className={classes.warning}>
              {getIconTypeForGoal(goal.goal) === "error" ? (
                <Error {...s.warningIcon} />
              ) : (
                <NotInterested {...s.warningIcon} />
              )}
              <span>{goal.message}</span>
            </div>
          );
        })}
      </div>
    );
  };

  private renderEvent = (event: ISchema.IEvent) => {
    const { classes } = this.props;

    const startTime = new ZonedMoment(event.startTime.millis);
    const endTime = new ZonedMoment(event.endTime.millis);

    const formattedDate = event.isAllDay
      ? startTime.format("YYYY-MM-DD") === endTime.format("YYYY-MM-DD")
        ? getAbsoluteDate(endTime, undefined, true)
        : getAbsoluteDate(startTime, endTime, true)
      : getAbsoluteDateTime(startTime, endTime);

    return (
      <div className={classes.event}>
        <div className={classes.orangeBar}></div>
        <div>
          <div className={classes.title}>{event.title}</div>
          <div className={classes.date}>{formattedDate}</div>
        </div>
      </div>
    );
  };

  public render() {
    const { classes, referencedEvents, autopilotMeetingKnob } = this.props;

    const event = referencedEvents.find(
      (e) => e.eventKey.externalEventId === autopilotMeetingKnob.externalEventId,
    );
    if (!event) {
      logger.error("cannot find referenced event for autopilot meeting knob", {
        referencedEvents,
        autopilotMeetingKnob,
      });
      return "";
    }

    return (
      <div className={classes.container}>
        <H2 style={s.styles.header}>Allow Clockwise to optimize the timing of this event</H2>
        {this.renderEvent(event)}
        {this.renderPain()}
        {this.renderAction(event)}
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(AutopilotSuggestionEnableMeetingComponent);

//////////////////
// WITH STYLES
//////////////////
const AutopilotSuggestionEnableMeetingStyled = withStyles(s.styles)(reduxContainer);

//////////////////
// RELAY
//////////////////
export const AutopilotSuggestionEnableMeeting = createFragmentContainer<IContainer>(
  AutopilotSuggestionEnableMeetingStyled,
  fragments,
);
